import { Box, CircularProgress, LinearProgress, Pagination, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { type ChangeEvent, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react"
import { useShallow } from "zustand/react/shallow"

import { calculateTotalPricingVariations } from "../utils"

import { ProductPricingTable } from "./tables"
import type { ProductPricingTableHandle } from "./tables/ProductPricingTable"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import type {
  BulkPriceChangeUpdateInput,
  GetPriceChangeItemsInput,
  PriceChangeFilterInput,
  PriceChangeType,
  Product,
  SubmitPriceChangeItemInput,
} from "@/graphql/codegen/graphql"
import { useNavigationGuard } from "@/hooks/useNavigationGuard"
import { type PricingDialogs, SUBMIT_CONFIRMATION_TO_DIALOG } from "@/screens/Pricing/constants"
import { useGetPriceChangeItems } from "@/screens/Pricing/hooks/useGetPriceChangeItems"
import { selectDefaultedRowCount, usePriceChangeTablesStore, usePriceTableEditsStore } from "@/screens/Pricing/stores"
import {
  calculateInvalidTiersCount,
  calculateModifiedTiersCount,
  getNextSubmitConfirmation,
  toPriceChangeSubmitItem,
} from "@/screens/Pricing/utils/priceChangeSubmit"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { isNil } from "@/utils/utils"

export interface PriceChangeSubmitContext {
  filter: PriceChangeFilterInput
  priceChangeItems: SubmitPriceChangeItemInput[]
}

export interface PriceChangeTablesHandle {
  getSubmitContext: () => PriceChangeSubmitContext | null
  refetch: () => void
  clearChanges: () => void
  resetInvalidEffectiveDates: () => void
  reseed: () => void
}

interface PriceChangeTablesProps {
  setDialogOpen: (open: PricingDialogs) => void
  openSubmitDialog: () => void
  setSelectedProduct: (product: Product) => void
  readonly?: boolean
  bulkUpdate?: BulkPriceChangeUpdateInput
}

export const PriceChangeTables = forwardRef<PriceChangeTablesHandle, PriceChangeTablesProps>(
  ({ setDialogOpen, setSelectedProduct, openSubmitDialog, readonly, bulkUpdate }, ref) => {
    const hasPendingBulkUpdate = Boolean(bulkUpdate)
    const uoms = useUnitOfMeasurementsStore(useShallow((s) => s.units))
    const navigate = useNavigate({ from: "/app/pricing/price-change" })
    const tableRefs = useRef<(ProductPricingTableHandle | null)[]>([])

    const { selectedCounts, resetVersion, reset, setSelectedCount, setIsFetching } = usePriceChangeTablesStore(
      useShallow((s) => ({
        selectedCounts: s.selectedCounts,
        resetVersion: s.resetVersion,
        reset: s.reset,
        setSelectedCount: s.setSelectedCount,
        setIsFetching: s.setIsFetching,
      }))
    )

    const clearEdits = usePriceTableEditsStore((s) => s.clearEdits)
    const priceChangeEdits = usePriceTableEditsStore((s) => s.edits)

    // Total persisted edits across all pages — persisted edits are the single source of truth
    // for submission-readiness. A single edit persists its whole group, so this can be larger
    // than the number of tiers actually touched — see modifiedTiersCount for that.
    const { modifiedTiersCount, totalInvalidCount, hasAnyChanges } = useMemo(
      () => ({
        modifiedTiersCount: calculateModifiedTiersCount(priceChangeEdits),
        totalInvalidCount: calculateInvalidTiersCount(priceChangeEdits),
        hasAnyChanges: Object.keys(priceChangeEdits).length > 0,
      }),
      [priceChangeEdits]
    )

    const handleSelectionChange = useCallback(
      (id: string, info: { selected: number; total: number }) => {
        setSelectedCount(id, info.selected)
      },
      [setSelectedCount]
    )

    const onNavigationConfirmHandler = useCallback(() => {
      clearEdits()
      reset()
    }, [clearEdits, reset])
    const { disableBlock } = useNavigationGuard({
      shouldBlock: hasAnyChanges || hasPendingBulkUpdate,
      onNavigationConfirmHandler,
    })

    const search = useSearch({ from: "/app/pricing/_layout/price-change" }) as {
      changeType?: PriceChangeType
      supplierId?: string
      effectiveDate?: string
      productCategoryId?: string
      productClassId?: string
      customerIds?: string[]
      specificProductIds?: string[]
      shipFromId?: string
      shipToId?: string
      productManagerId?: string
      page?: number
    }
    const {
      changeType,
      supplierId,
      effectiveDate,
      productCategoryId,
      productClassId,
      customerIds = [],
      specificProductIds = [],
      shipFromId,
      shipToId,
      productManagerId,
      page = 1,
    } = search

    // Invalidate persisted edits when the filter changes (new result set makes prior
    // edits stale) — but NOT on page changes, which must preserve edits.
    const filterKey = JSON.stringify({
      changeType,
      supplierId,
      effectiveDate,
      productCategoryId,
      productClassId,
      customerIds,
      specificProductIds,
      shipFromId,
      shipToId,
      productManagerId,
    })
    const prevFilterKeyRef = useRef(filterKey)
    useEffect(() => {
      if (prevFilterKeyRef.current === filterKey) return
      prevFilterKeyRef.current = filterKey
      clearEdits()
      reset()
    }, [filterKey, clearEdits, reset])

    // Clear persisted edits when leaving the screen.
    useEffect(() => () => clearEdits(), [clearEdits])

    const filter: PriceChangeFilterInput | null = changeType
      ? {
          changeType,
          ...(supplierId ? { supplierId } : {}),
          ...(effectiveDate ? { effectiveDate } : {}),
          ...(productCategoryId ? { productCategoryId } : {}),
          ...(productClassId ? { productClassId } : {}),
          ...(customerIds && customerIds.length ? { customerIds } : {}),
          ...(specificProductIds && specificProductIds.length
            ? { specificProductIds: specificProductIds.map((v) => Number(v)) }
            : {}),
          ...(shipFromId ? { shipFromId } : {}),
          ...(shipToId ? { shipToId } : {}),
          ...(productManagerId ? { productManagerId } : {}),
        }
      : null

    const input: GetPriceChangeItemsInput | null = filter ? { filter } : null

    const { priceChangeItems, total, pageCount, isFetching, refetch } = useGetPriceChangeItems(input, page)

    useEffect(() => {
      setIsFetching(isFetching)
    }, [isFetching, setIsFetching])

    useImperativeHandle(ref, () => ({
      getSubmitContext: () => {
        if (!filter) return null
        // Build from the store so edits from every page are submitted, not just the
        // currently-mounted page.
        const edits = Object.values(usePriceTableEditsStore.getState().edits)
        const submitItems = edits.map((edit) =>
          toPriceChangeSubmitItem(edit.row, edit.productId, edit.group, edit.uomId, edit.priceChangeType)
        )

        return { filter, priceChangeItems: submitItems }
      },
      refetch: () => {
        clearEdits()
        reset()
        disableBlock()
        refetch()
      },
      clearChanges: () => {
        clearEdits()
        reset()
        disableBlock()
      },
      resetInvalidEffectiveDates: () => {
        usePriceTableEditsStore.getState().resetInvalidDates()
        tableRefs.current.forEach((tableRef) => tableRef?.resetInvalidDates())
        continueSubmit({ lapsedConfirmed: true })
      },
      reseed: () => {
        tableRefs.current.forEach((tableRef) => tableRef?.reseed())
      },
    }))

    const totalPricingVariations = calculateTotalPricingVariations(priceChangeItems)
    const totalSelected = (priceChangeItems ?? []).reduce((acc, pcItems) => acc + (selectedCounts[pcItems.id] ?? 0), 0)

    const handlePageChange = (_: ChangeEvent<unknown>, value: number) => {
      navigate({
        search: (prev) => ({ ...prev, page: value === 1 ? undefined : value }),
        replace: true,
      })
    }

    function continueSubmit({ lapsedConfirmed }: { lapsedConfirmed: boolean }) {
      const confirmation = getNextSubmitConfirmation({
        edits: usePriceTableEditsStore.getState().edits,
        defaultedRowCount: selectDefaultedRowCount(usePriceTableEditsStore.getState()),
        lapsedConfirmed,
      })
      const confirmationDialog = isNil(confirmation) ? undefined : SUBMIT_CONFIRMATION_TO_DIALOG[confirmation]
      if (confirmationDialog) {
        setDialogOpen(confirmationDialog)
      } else {
        openSubmitDialog()
      }
    }

    const handleSubmit = async () => {
      await Promise.all(tableRefs.current.map((tableRef) => tableRef?.flushPendingEdits()))
      const edits = usePriceTableEditsStore.getState().edits

      if (calculateInvalidTiersCount(edits) > 0) {
        return
      }
      continueSubmit({ lapsedConfirmed: false })
    }

    const canSubmit = (hasAnyChanges || hasPendingBulkUpdate) && !readonly && totalInvalidCount === 0

    if (isFetching) {
      return (
        <Box className='flex items-center justify-center p-16'>
          <CircularProgress />
        </Box>
      )
    }

    if (priceChangeItems === null) {
      return (
        <InformationMissingAlert title='Apply filters, optionally set Bulk Update Options, then review and adjust individual items.' />
      )
    }

    if (!priceChangeItems.length) {
      return <InformationMissingAlert title='No results found. Try adjusting the filters.' />
    }

    return (
      <Box className='flex flex-col'>
        <Box className='sticky top-0 z-10 flex shrink-0 items-center justify-between bg-white pb-2 shadow-[0_4px_6px_-2px_rgba(0,0,0,0.15)]'>
          <Box className='flex flex-col gap-1'>
            <Typography variant='body1'>
              Showing {priceChangeItems.length} of {total} products, {totalPricingVariations} total pricing variations.
            </Typography>
            {totalPricingVariations > 0 && (
              <Typography variant='body2' className='text-gray-600'>
                {totalSelected} of {totalPricingVariations} selected
              </Typography>
            )}
          </Box>

          <Box className='flex flex-col items-end gap-1'>
            <Box className='flex items-center gap-3'>
              {modifiedTiersCount > 0 && (
                <Typography variant='body2' className='text-gray-600'>
                  {modifiedTiersCount} manually modified {modifiedTiersCount === 1 ? "tier" : "tiers"}
                </Typography>
              )}

              <Button variant='primary' onClick={handleSubmit} disabled={!canSubmit}>
                Submit Price Changes
              </Button>
            </Box>
            {totalInvalidCount > 0 && !readonly && (
              <Typography variant='body2' className='mt-3 text-red-600'>
                {totalInvalidCount} required {totalInvalidCount === 1 ? "cell needs" : "cells need"} attention before
                submitting.
              </Typography>
            )}
          </Box>

          {isFetching && <LinearProgress className='absolute inset-x-0 bottom-0' />}
        </Box>

        <Box className='flex w-full flex-col py-2'>
          {priceChangeItems.map((pcItems, index) => (
            <Box key={`${pcItems.id}-${resetVersion}`} className='mb-6'>
              <ProductPricingTable
                ref={(el: ProductPricingTableHandle | null) => {
                  tableRefs.current[index] = el
                }}
                priceChangeItems={pcItems}
                uoms={uoms}
                setDialogOpen={setDialogOpen}
                setSelectedProduct={setSelectedProduct}
                onSelectionChange={handleSelectionChange}
                readonly={readonly}
                priceChangeType={changeType as PriceChangeType}
                persistEdits={!readonly}
                bulkUpdate={bulkUpdate}
              />
            </Box>
          ))}
        </Box>

        <Box className='sticky bottom-0 z-10 flex shrink-0 items-center justify-center bg-white pt-4 shadow-[0_-4px_6px_-2px_rgba(0,0,0,0.15)]'>
          <Pagination
            count={Math.max(pageCount, 1)}
            showFirstButton
            showLastButton
            page={page}
            onChange={handlePageChange}
            color='primary'
            data-testid='price-change.tables.pagination'
          />
        </Box>
      </Box>
    )
  }
)

PriceChangeTables.displayName = "PriceChangeTables"
